/**
 * Route Configuration
 *
 * Centralized route definitions for the application.
 * Use these constants instead of hardcoding paths.
 */

// =============================================================================
// ROUTE PATHS
// =============================================================================

export const ROUTES = {
  // Public routes
  HOME: '/',
  LOGIN: '/login',
  // One redemption URL for every package and code, not a per-package link. The
  // code identifies the package, so this is never linked from nav.
  REDEEM: '/redeem',
  REGISTER: '/register',
  RESET_PASSWORD: '/reset-password',
  RESET_PASSWORD_CONFIRM: '/reset-password/confirm',

  // Protected routes
  DASHBOARD: '/dashboard',
  SESSIONS: '/sessions',
  SESSION_INTERVIEW: '/sessions/:assessmentId/interview',
  SESSION_RESULTS: '/sessions/:assessmentId/results',
  // Same Sessions/SessionInterview/SessionResults components as above, reused
  // verbatim for a code-redeemed guest (see useSessionToken) — only the URL
  // differs. See hooks/useSessionRoutes.ts, which is what every in-app link
  // between these actually navigates through.
  GUEST_SESSIONS: '/guest-interview',
  GUEST_SESSION_INTERVIEW: '/guest-interview/:assessmentId/interview',
  GUEST_SESSION_RESULTS: '/guest-interview/:assessmentId/results',
  PACKAGES: '/packages',
  SETTINGS: '/settings',
  PROFILE: '/profile',

  // Evaluate
  EVALUATE: '/evaluate',

  // Admin routes
  ADMIN: '/admin',
  ADMIN_USERS: '/admin/users',
  ADMIN_ORGANIZATIONS: '/admin/organizations',
  ADMIN_ASSESSMENTS: '/admin/assessments',
  ADMIN_SESSION_RESULTS: '/admin/sessions/:assessmentId/results',
  ADMIN_HTS_SUBMISSION_RESULTS: '/admin/hts-submissions/:submissionId/results',
  ADMIN_QUESTIONS: '/admin/questions',
  ADMIN_QUESTION_SETS: '/admin/question-sets',
  ADMIN_QUESTION_SET_NEW: '/admin/question-sets/new',
  ADMIN_QUESTION_SET_EDIT: '/admin/question-sets/:id',
  ADMIN_PACKAGES: '/admin/packages',
  ADMIN_PACKAGE_NEW: '/admin/packages/new',
  ADMIN_PACKAGE_EDIT: '/admin/packages/:id',
  ADMIN_TRACES: '/admin/traces',
  ADMIN_TRACE_DETAIL: '/admin/traces/:traceId',
  ADMIN_HTS_QUESTIONNAIRES: '/admin/hts-questionnaires',
  ADMIN_HTS_QUESTIONNAIRE_NEW: '/admin/hts-questionnaires/new',
  ADMIN_HTS_QUESTIONNAIRE_EDIT: '/admin/hts-questionnaires/:id',

  // HTS Questionnaire — reached programmatically only, not linked from nav
  HTS_QUESTIONNAIRE: '/hts',
  HTS_READINESS: '/hts/:submissionId/readiness',

  // Auth utility routes
  VERIFY_DEVICE: '/verify-device',

  // Utility routes
  NOT_FOUND: '/404',
  UNAUTHORIZED: '/401',

  // Temporary — quick typography reference, not linked from any nav. See
  // apps/web/CLAUDE.md before removing: safe to delete once no longer needed.
  STYLE_GUIDE: '/dev/style-guide',
} as const;

export type RoutePath = (typeof ROUTES)[keyof typeof ROUTES];

// =============================================================================
// ROUTE HELPERS
// =============================================================================

/**
 * Build a route path with parameters
 *
 * @example
 * buildRoute('/users/:id', { id: '123' }) // '/users/123'
 */
export function buildRoute(
  path: string,
  params: Record<string, string | number>
): string {
  let result = path;

  Object.entries(params).forEach(([key, value]) => {
    result = result.replace(`:${key}`, String(value));
  });

  return result;
}

/**
 * Build a route with query parameters
 *
 * @example
 * buildRouteWithQuery('/search', { q: 'test', page: 1 }) // '/search?q=test&page=1'
 */
export function buildRouteWithQuery(
  path: string,
  query: Record<string, string | number | boolean | undefined>
): string {
  const params = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined) {
      params.append(key, String(value));
    }
  });

  const queryString = params.toString();
  return queryString ? `${path}?${queryString}` : path;
}

// =============================================================================
// ROUTE METADATA
// =============================================================================

export interface RouteConfig {
  path: string;
  title: string;
  requiresAuth: boolean;
  requiredRoles?: string[];
  layout?: 'main' | 'auth' | 'none';
}

/**
 * Route metadata for each route
 * Used for breadcrumbs, page titles, access control, etc.
 */
export const routeConfigs: Record<string, RouteConfig> = {
  [ROUTES.HOME]: {
    path: ROUTES.HOME,
    title: 'Home',
    requiresAuth: false,
    layout: 'none',
  },
  [ROUTES.LOGIN]: {
    path: ROUTES.LOGIN,
    title: 'Login',
    requiresAuth: false,
    layout: 'auth',
  },
  [ROUTES.REDEEM]: {
    path: ROUTES.REDEEM,
    title: 'Enter Access Code',
    requiresAuth: false,
    layout: 'none',
  },
  [ROUTES.REGISTER]: {
    path: ROUTES.REGISTER,
    title: 'Register',
    requiresAuth: false,
    layout: 'auth',
  },
  [ROUTES.RESET_PASSWORD]: {
    path: ROUTES.RESET_PASSWORD,
    title: 'Reset Password',
    requiresAuth: false,
    layout: 'auth',
  },
  [ROUTES.DASHBOARD]: {
    path: ROUTES.DASHBOARD,
    title: 'Dashboard',
    requiresAuth: true,
    layout: 'main',
  },
  [ROUTES.SESSIONS]: {
    path: ROUTES.SESSIONS,
    title: 'Sessions',
    requiresAuth: true,
    layout: 'main',
  },
  [ROUTES.SESSION_INTERVIEW]: {
    path: ROUTES.SESSION_INTERVIEW,
    title: 'Interview',
    requiresAuth: true,
    layout: 'main',
  },
  [ROUTES.SESSION_RESULTS]: {
    path: ROUTES.SESSION_RESULTS,
    title: 'Results',
    requiresAuth: true,
    layout: 'main',
  },
  // Auth-required like their non-guest counterparts: App.tsx nests all three
  // inside ProtectedRoute, which needs a valid guest session token to let them
  // through (see hooks/useSessionToken.ts). Not Clerk auth, but auth.
  [ROUTES.GUEST_SESSIONS]: {
    path: ROUTES.GUEST_SESSIONS,
    title: 'Sessions',
    requiresAuth: true,
    layout: 'main',
  },
  [ROUTES.GUEST_SESSION_INTERVIEW]: {
    path: ROUTES.GUEST_SESSION_INTERVIEW,
    title: 'Interview',
    requiresAuth: true,
    layout: 'main',
  },
  [ROUTES.GUEST_SESSION_RESULTS]: {
    path: ROUTES.GUEST_SESSION_RESULTS,
    title: 'Results',
    requiresAuth: true,
    layout: 'main',
  },
  [ROUTES.PACKAGES]: {
    path: ROUTES.PACKAGES,
    title: 'Packages',
    requiresAuth: true,
    layout: 'main',
  },
  [ROUTES.SETTINGS]: {
    path: ROUTES.SETTINGS,
    title: 'Settings',
    requiresAuth: true,
    layout: 'main',
  },
  [ROUTES.PROFILE]: {
    path: ROUTES.PROFILE,
    title: 'Profile',
    requiresAuth: true,
    layout: 'main',
  },
  [ROUTES.ADMIN]: {
    path: ROUTES.ADMIN,
    title: 'Admin',
    requiresAuth: true,
    requiredRoles: ['SUPER_ADMIN'],
    layout: 'main',
  },
  [ROUTES.ADMIN_USERS]: {
    path: ROUTES.ADMIN_USERS,
    title: 'User Management',
    requiresAuth: true,
    requiredRoles: ['SUPER_ADMIN'],
    layout: 'main',
  },
  [ROUTES.ADMIN_ASSESSMENTS]: {
    path: ROUTES.ADMIN_ASSESSMENTS,
    title: 'Assessment Results',
    requiresAuth: true,
    requiredRoles: ['SUPER_ADMIN', 'COACH'],
    layout: 'main',
  },
  [ROUTES.ADMIN_SESSION_RESULTS]: {
    path: ROUTES.ADMIN_SESSION_RESULTS,
    title: 'Session Results (Admin)',
    requiresAuth: true,
    requiredRoles: ['SUPER_ADMIN', 'COACH'],
    layout: 'main',
  },
  [ROUTES.ADMIN_HTS_SUBMISSION_RESULTS]: {
    path: ROUTES.ADMIN_HTS_SUBMISSION_RESULTS,
    title: 'HTS Submission (Admin)',
    requiresAuth: true,
    requiredRoles: ['SUPER_ADMIN', 'COACH'],
    layout: 'main',
  },
  [ROUTES.ADMIN_QUESTIONS]: {
    path: ROUTES.ADMIN_QUESTIONS,
    title: 'Question Bank',
    requiresAuth: true,
    requiredRoles: ['SUPER_ADMIN'],
    layout: 'main',
  },
  [ROUTES.ADMIN_QUESTION_SETS]: {
    path: ROUTES.ADMIN_QUESTION_SETS,
    title: 'Question Sets',
    requiresAuth: true,
    requiredRoles: ['SUPER_ADMIN'],
    layout: 'main',
  },
  [ROUTES.ADMIN_TRACES]: {
    path: ROUTES.ADMIN_TRACES,
    title: 'Traces',
    requiresAuth: true,
    requiredRoles: ['SUPER_ADMIN'],
    layout: 'main',
  },
  [ROUTES.ADMIN_QUESTION_SET_NEW]: {
    path: ROUTES.ADMIN_QUESTION_SET_NEW,
    title: 'Add Question Set',
    requiresAuth: true,
    requiredRoles: ['SUPER_ADMIN'],
    layout: 'main',
  },
  [ROUTES.ADMIN_QUESTION_SET_EDIT]: {
    path: ROUTES.ADMIN_QUESTION_SET_EDIT,
    title: 'Edit Question Set',
    requiresAuth: true,
    requiredRoles: ['SUPER_ADMIN'],
    layout: 'main',
  },
  [ROUTES.ADMIN_PACKAGES]: {
    path: ROUTES.ADMIN_PACKAGES,
    title: 'Packages',
    requiresAuth: true,
    requiredRoles: ['SUPER_ADMIN'],
    layout: 'main',
  },
  [ROUTES.ADMIN_PACKAGE_NEW]: {
    path: ROUTES.ADMIN_PACKAGE_NEW,
    title: 'Add Package',
    requiresAuth: true,
    requiredRoles: ['SUPER_ADMIN'],
    layout: 'main',
  },
  [ROUTES.ADMIN_PACKAGE_EDIT]: {
    path: ROUTES.ADMIN_PACKAGE_EDIT,
    title: 'Edit Package',
    requiresAuth: true,
    requiredRoles: ['SUPER_ADMIN'],
    layout: 'main',
  },
  [ROUTES.ADMIN_TRACE_DETAIL]: {
    path: ROUTES.ADMIN_TRACE_DETAIL,
    title: 'Trace Detail (Admin)',
    requiresAuth: true,
    requiredRoles: ['SUPER_ADMIN'],
    layout: 'main',
  },
  [ROUTES.ADMIN_HTS_QUESTIONNAIRES]: {
    path: ROUTES.ADMIN_HTS_QUESTIONNAIRES,
    title: 'HTS Questionnaires',
    requiresAuth: true,
    requiredRoles: ['SUPER_ADMIN'],
    layout: 'main',
  },
  [ROUTES.ADMIN_HTS_QUESTIONNAIRE_NEW]: {
    path: ROUTES.ADMIN_HTS_QUESTIONNAIRE_NEW,
    title: 'Add HTS Questionnaire',
    requiresAuth: true,
    requiredRoles: ['SUPER_ADMIN'],
    layout: 'main',
  },
  [ROUTES.ADMIN_HTS_QUESTIONNAIRE_EDIT]: {
    path: ROUTES.ADMIN_HTS_QUESTIONNAIRE_EDIT,
    title: 'Edit HTS Questionnaire',
    requiresAuth: true,
    requiredRoles: ['SUPER_ADMIN'],
    layout: 'main',
  },
  [ROUTES.HTS_QUESTIONNAIRE]: {
    path: ROUTES.HTS_QUESTIONNAIRE,
    title: 'HTS Questionnaire',
    requiresAuth: true,
    layout: 'none',
  },
  [ROUTES.HTS_READINESS]: {
    path: ROUTES.HTS_READINESS,
    title: 'Your Readiness Summary',
    requiresAuth: true,
    layout: 'none',
  },
};

/**
 * Get route config by path
 */
export function getRouteConfig(path: string): RouteConfig | undefined {
  return routeConfigs[path];
}

/**
 * Check if a route requires authentication
 */
export function isProtectedRoute(path: string): boolean {
  const config = getRouteConfig(path);
  return config?.requiresAuth ?? false;
}

/**
 * Check if user has access to a route
 */
export function hasRouteAccess(path: string, userRoles: string[]): boolean {
  const config = getRouteConfig(path);

  if (!config) {
    return true; // Unknown routes are accessible
  }

  if (!config.requiredRoles || config.requiredRoles.length === 0) {
    return true; // No role requirements
  }

  return config.requiredRoles.some((role) => userRoles.includes(role));
}
